import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { AddressLink } from "@/components/AddressLink";
import { TAGLINE } from "@/components/Pillars";

const links = [
  { label: "Home", path: "/" },
  { label: "Our Story", path: "/story" },
  { label: "Our Homes", path: "/homes" },
  { label: "Services", path: "/services" },
  { label: "Admissions", path: "/admissions" },
  { label: "Dedication Wall", path: "/wall" },
  { label: "Contact", path: "/contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-purple-100/80 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <img src="/logo.png" alt="Jaliyah's House of Hope logo" className="h-10 sm:h-12 w-auto" />
            <span className="hidden md:block leading-tight">
              <span className="block font-display font-bold text-[#2E1065] text-sm">Jaliyah's House of Hope</span>
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#B45309] font-bold" data-testid="header-tagline">{TAGLINE}</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
            {links.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.path === "/" ? "home" : l.path.slice(1)}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold transition-colors duration-300 whitespace-nowrap ${
                    isActive ? "text-[#5B21B6]" : "text-slate-600 hover:text-[#5B21B6]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="start-referral-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-bold px-5 py-2.5 rounded-full shadow-md transition-shadow duration-300 hover:shadow-amber-500/30 hover:shadow-lg whitespace-nowrap"
            >
              Start Referral
            </Link>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#2E1065]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden bg-white border-t border-purple-100"
            >
              <div className="px-6 py-4 flex flex-col gap-4">
                {links.map((l) => (
                  <NavLink
                    key={l.path}
                    to={l.path}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.path === "/" ? "home" : l.path.slice(1)}`}
                    className={({ isActive }) =>
                      `text-base font-semibold ${isActive ? "text-[#5B21B6]" : "text-slate-700"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-start-referral-btn"
                  className="bg-amber-500 text-slate-950 text-center text-sm font-bold px-5 py-3 rounded-full"
                >
                  Start Referral
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">{children}</main>

      <footer data-testid="site-footer" className="bg-[#2E1065] text-[#FAF8F5] relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <img src="/logo.png" alt="Jaliyah's House of Hope" className="h-16 w-auto mb-4 bg-white rounded-xl p-1" />
            <p className="font-display italic text-amber-400 text-lg mb-3" data-testid="footer-tagline">{TAGLINE}</p>
            <p className="text-sm text-purple-200 leading-relaxed max-w-xs">
              Safe Spaces, Stronger Tomorrows. An ASAM 3.3 clinically managed high-intensity residential
              community honoring the life and light of Jaliyah Ikja Mason.
            </p>
          </div>
          <div>
            <h4 className="font-display font-bold text-lg mb-4 text-amber-400">Explore</h4>
            <ul className="space-y-2 text-sm">
              {links.map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-purple-200 hover:text-amber-400 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/admin" data-testid="footer-admin-link" className="text-purple-200 hover:text-amber-400 transition-colors">
                  Staff Login
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold text-lg mb-4 text-amber-400">Reach Us</h4>
            <ul className="space-y-2 text-sm text-purple-200">
              <li data-testid="footer-email">admin@jaliyahshouseofhopecom.org</li>
              <li data-testid="footer-address">
                <span className="block text-purple-300 text-xs mb-0.5">The Male Manor</span>
                <AddressLink testId="footer-address-link" className="text-purple-200 hover:text-amber-300" />
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-purple-300 flex items-center justify-center gap-2">
          <Heart size={12} className="text-amber-400 fill-amber-400" />
          In loving memory of Jaliyah · © {new Date().getFullYear()} Jaliyah's House of Hope LLC
        </div>
      </footer>
    </div>
  );
}
